import { memo, useEffect, useState } from 'react'

import {
  type V1FormElementState,
  type V1FormElementUiTextField,
  type V1FormElementContentState,
  V1FormElementValidateType,
  V1FormElementSetLayoutDirection,
  V1FormElementStateType
} from 'mnvm-mypage-app-ts-if'
import { InputText } from '@/components/base/Form/InputText'
import { Text3 } from '@/components/base/Text'
import { align, display } from '@/styles/display'
import spaceStyle from '@/styles/space'
import { showFormValue } from '../formText'
import textAlign from '@/styles/text_align'
import { fontSize, fontWeight } from '@/styles/font'
import { Block } from '@/components/base/Block'
import AppAssets from '@/styles/app_assets'
import useIsMobile from '@/hooks/useMobile'

export interface FormElementTextFieldProps {
  label?: string
  isEdit?: boolean
  name: string
  textFieldUiIFM: V1FormElementUiTextField
  formElementState: V1FormElementState
  elemenErrorMessage?: string
  validateType?: V1FormElementValidateType
  onChange: (newElementState: V1FormElementState) => void
  style?: TextFieldStyle
  formElementSetLayout?: string
}

export interface TextFieldStyle {
  flexGrow?: number
}

const formatJapaneseTel = (value: string): string => {
  // Format the value as a Japanese telephone number
  const cleaned = value.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{2,3})(\d{1,4})(\d{4})$/)
  if (match != null) {
    return `${match[1]}-${match[2]}-${match[3]}`
  }
  return value
}

const FormElementTextField = ({
  label,
  isEdit = true,
  textFieldUiIFM,
  formElementState,
  elemenErrorMessage,
  validateType,
  onChange,
  name,
  style,
  formElementSetLayout
}: FormElementTextFieldProps): JSX.Element => {
  const isMobile = useIsMobile()
  const stateValue =
    formElementState.contentStates?.at(0)?.text ??
    formElementState.contentStates?.at(0)?.uint32 ??
    ''
  const [valueFormatted, setValueFormatted] = useState<string | number>('')
  const unit = textFieldUiIFM.unit
  const separator = textFieldUiIFM.seperator
  const prefix = textFieldUiIFM.prefix
  const prefixWidth = isMobile ? (isEdit ? '60%' : '40%') : '50%'

  const isWebStyleThreeColumns =
    !isMobile &&
    formElementSetLayout === V1FormElementSetLayoutDirection.ThreeColumns

  useEffect(() => {
    if (validateType === V1FormElementValidateType.Tel) {
      const formattedValue = formatJapaneseTel(String(stateValue))
      setValueFormatted(formattedValue)
    }
  }, [stateValue])

  if (!isEdit) {
    return (
      <Block
        display={display.FLEX}
        alignItems={align.CENTER}
        textAlign={align.LEFT}
        justifyContent={
          formElementSetLayout === V1FormElementSetLayoutDirection.ThreeColumns
            ? align.SPACE_BETWEEN
            : undefined
        }
      >
        {prefix !== undefined && (
          <Block width={prefixWidth} textAlign={textAlign.LEFT}>
            <Text3 margin={{ right: spaceStyle.M }}>{prefix}</Text3>
          </Block>
        )}
        <Block
          display={display.FLEX}
          margin={{
            right: isWebStyleThreeColumns ? spaceStyle.XXXXXL : spaceStyle.NONE
          }}
        >
          <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
            {showFormValue(
              validateType === V1FormElementValidateType.Tel
                ? valueFormatted
                : stateValue
            )}
          </Text3>
          {unit !== undefined && (
            <Text3 margin={{ all: spaceStyle.NONE }}>{unit}</Text3>
          )}
        </Block>

        {separator !== undefined && (
          <Text3 margin={{ horizontal: spaceStyle.M }}>{separator}</Text3>
        )}
      </Block>
    )
  }

  if (formElementState.type === V1FormElementStateType.Text) {
    const onChangeText = (value: string): void => {
      let submitValue = value
      if (validateType === V1FormElementValidateType.Tel) {
        submitValue = submitValue.replaceAll('-', '').slice(0, 11)
      }
      onChange({
        ...formElementState,
        contentStates: [
          {
            text: submitValue
          } satisfies V1FormElementContentState
        ]
      })
    }

    const textFieldLabel = (
      <Text3
        textAlign={textAlign.LEFT}
        fontWeight={fontWeight.Bold}
        margin={{ all: spaceStyle.NONE }}
        padding={{ bottom: spaceStyle.XS }}
      >
        {label}
      </Text3>
    )

    return (
      <>
        {textFieldLabel}
        <Block
          display={display.FLEX}
          alignItems={align.CENTER}
          textAlign={textAlign.LEFT}
          margin={{
            right: isWebStyleThreeColumns ? spaceStyle.XXXXXL : spaceStyle.NONE
          }}
        >
          {prefix !== undefined && (
            <Block width={prefixWidth} textAlign={textAlign.LEFT}>
              <Text3 margin={{ right: spaceStyle.M }}>{prefix}</Text3>
            </Block>
          )}
          <InputText
            maxWidth="100%"
            type={'text'}
            value={
              validateType === V1FormElementValidateType.Tel
                ? valueFormatted
                : stateValue
            }
            name={name}
            onChange={(value) => {
              onChangeText(value)
            }}
            placeholder={textFieldUiIFM?.placeholder}
            prefixIcon={
              formElementState.key === 'keyword_text'
                ? AppAssets.icSearch
                : undefined
            }
            placeholderFontSize={
              formElementState.key === 'keyword_text' ? fontSize.S : undefined
            }
            unit={unit}
            unitWidth={
              formElementSetLayout ===
              V1FormElementSetLayoutDirection.ThreeColumns
                ? isMobile
                  ? '30%'
                  : '20%'
                : undefined
            }
            suffixSeparator={separator}
            errorMessage={elemenErrorMessage}
            style={{ flexGrow: style?.flexGrow }}
          />
        </Block>
      </>
    )
  }

  if (formElementState.type === V1FormElementStateType.Uint32) {
    const onChangeText = (value: number): void => {
      onChange({
        ...formElementState,
        contentStates: [
          {
            uint32: value
          } satisfies V1FormElementContentState
        ]
      })
    }
    return (
      <InputText
        maxWidth="100%"
        type={'number'}
        value={
          validateType === V1FormElementValidateType.Tel
            ? valueFormatted
            : stateValue
        }
        name={name}
        onChange={(value) => {
          onChangeText(Number(value))
        }}
        placeholder={textFieldUiIFM?.placeholder}
        unit={unit}
      />
    )
  }

  return (
    <>
      <div>not implemented</div>
    </>
  )
}

export default memo(FormElementTextField)
